/* The time zone this person sees times in (Settings → Time zone; owner decision 2026-10-13). A display preference only:
   the database always stores Chicago wall clock + UTC offset + duration, and Reports / exports open in Chicago.
   Saved in the database (member_prefs, migration 014) so it follows the person to every device; a copy is kept on the
   device for the first paint and offline, but the database value wins. Signed out = Chicago again.
   With Chicago (the default) every helper here returns exactly what the app showed before. */
import type {Entry} from './lib/model.ts';
import {WS_TZ, wallOf, todayStr, entryStartMs, offsetAtMs} from './lib/time.ts';
import {zoneTime, zoneShort} from './lib/zonenames.ts';
import {zoneInfo, zoneCode} from './lib/zonepick.ts';

let tz = WS_TZ;
export const myTz = () => tz;
export const isChicago = () => tz === WS_TZ;
const KEY = (user: string) => 'ot_tz_' + user;

/** a zone this browser knows (Intl) — anything else is ignored */
export function validZone(z: unknown): z is string {
  if (typeof z !== 'string' || !z) return false;
  try { new Intl.DateTimeFormat('en-US', {timeZone: z}); return true; } catch { return false; }
}
let changes = 0;
/** goes up on every change: a database answer asked for before a change made here must not undo it */
export const tzChanges = () => changes;
/** use this zone from now on (and remember it on this device for this login) */
export function setMyTz(z: string | null | undefined, user?: string | null) {
  tz = validZone(z) ? z : WS_TZ; changes++;
  if (user) try { localStorage.setItem(KEY(user), tz); } catch { /* private mode */ }
}
/** the zone remembered on this device for this login (first paint, offline) */
export function cachedTz(user: string): string | null {
  try { const z = localStorage.getItem(KEY(user)); return validZone(z) ? z : null; } catch { return null; }
}
/** signed out: back to Chicago, and the device copy for that login is forgotten */
export function forgetTz(user?: string | null) {
  tz = WS_TZ;
  try { if (user) localStorage.removeItem(KEY(user)); else Object.keys(localStorage).filter(k => k.startsWith('ot_tz_')).forEach(k => localStorage.removeItem(k)); } catch { /* ignore */ }
}

/** today's date in this person's zone (the Timer's "Today", new entries' date) */
export const myToday = () => todayStr(tz);
/** an instant as wall-clock time in this person's zone */
export const myWall = (ms: number) => wallOf(ms, tz);

/** an entry's day and clock times as this person sees them. Chicago: exactly the stored values (unchanged). Another
    zone: worked out from the real instants (start = stored wall + offset; end = start + duration, never the end clock). */
const seen = new WeakMap<Entry, {tz: string; v: {date: string; startedAt: string; endedAt: string}}>();
export function localOf(e: Entry): {date: string; startedAt: string; endedAt: string} {
  if (tz === WS_TZ) return {date: e.date, startedAt: e.startedAt, endedAt: e.endedAt};
  const c = seen.get(e); if (c && c.tz === tz) return c.v;
  const ms = entryStartMs(e.startedAt, e.so);
  const v = isNaN(ms) ? {date: e.date, startedAt: e.startedAt, endedAt: e.endedAt}
    : (() => { const s = wallOf(ms, tz); return {date: s.slice(0, 10), startedAt: s, endedAt: wallOf(ms + (e.durationSeconds || 0) * 1000, tz)}; })();
  seen.set(e, {tz, v});
  return v;
}

/* ---------- names and labels (owner request 2026-10-13: "Central", never "Chicago"; see lib/zonenames.ts) ---------- */
/** the zones offered first in Settings (then "This device's zone", then every zone) */
export const COMMON_ZONES: string[] = ['America/Chicago', 'America/New_York', 'America/Denver', 'America/Los_Angeles', 'Asia/Manila'];
/** how a sentence names this person's zone (or another): "Central time", "Philippine time" */
export const zoneName = (z = tz) => zoneTime(z);
/** a short name for buttons: "Central", "Manila" */
export const zoneShortName = (z = tz) => zoneShort(z);
/** a zone's line in the list: "Chicago (Central) — CDT · UTC−5" */
export const zoneLabel = (z: string, at = Date.now()) => zoneInfo(z, at).line;
/** this device's own zone (Intl), or '' when the browser does not say */
export function deviceZone(): string { try { const z = Intl.DateTimeFormat().resolvedOptions().timeZone; return validZone(z) ? z : ''; } catch { return ''; } }
/** every zone this browser knows (Intl.supportedValuesOf), with the common ones and the person's own always included */
export function allZones(): string[] {
  let list: string[] = [];
  try { list = (Intl as any).supportedValuesOf('timeZone') as string[]; } catch { /* older browser: the common ones only */ }
  return [...new Set([...COMMON_ZONES, tz, ...list])].sort();
}
/** "13 hours ahead of Central today", "1 hour behind Central today", "the same time as Central today" */
export function vsCentral(z: string, at = Date.now()): string {
  const d = offsetAtMs(at, z) - offsetAtMs(at, WS_TZ);
  if (!d) return 'the same time as Central today';
  const h = Math.abs(d) / 60, n = Number.isInteger(h) ? String(h) : h.toFixed(2).replace(/0$/, '');
  return `${n} hour${h === 1 ? '' : 's'} ${d > 0 ? 'ahead of' : 'behind'} Central today`;
}
/** Settings' summary: "Philippine time (PHT · UTC+8) — 13 hours ahead of Central today", "Central time (CDT · UTC−5) — the default" */
export function zoneSummary(z: string, at = Date.now()): string {
  return `${zoneTime(z)} (${zoneCode(z, at)}) — ${z === WS_TZ ? 'the default' : vsCentral(z, at)}`;
}
